import type { VoiceCallDirection, VoiceCallParticipant, VoiceDestination, VoiceMissedStatus } from '../voice/types';

export const ContactCenterRealtimeEvent = {
  InboxChanged: 'contact_center.inbox.changed',
  ConversationChanged: 'contact_center.conversation.changed',
  MessageCreated: 'contact_center.conversation.message_created',
  DeliveryUpdated: 'contact_center.conversation.delivery_updated',
  AssignmentChanged: 'contact_center.conversation.assignment_changed',
  CrmLinksChanged: 'contact_center.conversation.crm_links_changed',
  SlaChanged: 'contact_center.conversation.sla_changed',
  VoiceCallChanged: 'contact_center.voice.call_changed',
  BrowserRegistrationChanged: 'contact_center.voice.browser_registration_changed',
  EmployeeVoiceChanged: 'organization.voice.changed',
} as const;

export type ContactCenterRealtimeEventName = typeof ContactCenterRealtimeEvent[keyof typeof ContactCenterRealtimeEvent];

export type ContactCenterRealtimeConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'degraded';

export type ContactCenterRealtimeReason = 'message_created' | 'delivery_updated' | 'assignment_changed' | 'crm_links_changed' | 'lifecycle_changed' | 'connector_changed' | 'sla_changed';

export type ContactCenterRealtimePayload = {
  cursor: string;
  tenantId: string;
  conversationId: string;
  connectorId?: string;
  messageId?: string;
  reason: ContactCenterRealtimeReason;
  occurredAt: string;
};

export type ContactCenterRealtimeInboxPayload = {
  cursor: string;
  tenantId: string;
  occurredAt: string;
};

export type ContactCenterRealtimeCatchUpEvent = {
  cursor: string;
  tenantId: string;
  conversationId: string;
  connectorId: string | null;
  messageId: string | null;
  eventName: string;
  reason: string;
  occurredAt: string;
};

export type ContactCenterRealtimeEventList = {
  events: ContactCenterRealtimeCatchUpEvent[];
  latestCursor: string | null;
  hasMore: boolean;
};

export type ContactCenterVoiceCallState =
  | 'DIALING'
  | 'RINGING'
  | 'ANSWERED'
  | 'ENDED'
  | 'FAILED';

export type ContactCenterVoiceCallChangedPayload = {
  tenantId: string;
  connectorId: string;
  clientUserRef: string | null;
  eventSchemaVersion?: string | null;
  callSessionId: string;
  fromLineId: string | null;
  destination?: VoiceDestination | null;
  destinationE164?: string | null;
  direction?: VoiceCallDirection;
  fromNumber?: string | null;
  toNumber?: string | null;
  lineLabel?: string | null;
  callerPresentation?: string | null;
  callerName?: string | null;
  routeTargetType?: string | null;
  routeTargetId?: string | null;
  routeSlot?: string | null;
  routeDecisionReason?: string | null;
  endpointDeviceId?: string | null;
  endpointType?: string | null;
  state: ContactCenterVoiceCallState;
  stateSequence: number;
  lastEventAt: string;
  failureCategory: string | null;
  failureMessage: string | null;
  participants?: VoiceCallParticipant[];
  winningParticipantId?: string | null;
  winningEndpointDeviceId?: string | null;
  availableCommands?: string[];
  answeredAt?: string | null;
  endedAt?: string | null;
  durationSeconds?: number | null;
  missedStatus?: VoiceMissedStatus | null;
};

export type EmployeeVoiceChangedPayload = {
  tenantId: string;
  tenantMembershipId: string;
  clientUserRef?: string | null;
  readinessVersion: number;
  readinessState: string;
  voiceEnabled: boolean;
  occurredAt: string;
};

export type ContactCenterBrowserRegistrationStatus =
  | 'issued'
  | 'registered'
  | 'retiring'
  | 'revoked'
  | 'expired'
  | 'failed';

/** Secret-free exact-generation Browser registration projection. */
export type ContactCenterBrowserRegistrationChangedPayload = {
  tenantId: string;
  connectorId: string;
  installationId: string;
  generation: number;
  sessionId: string;
  status: ContactCenterBrowserRegistrationStatus;
  lifecycleState: string;
  stateVersion: number;
  eventId: string;
  eventSchemaVersion: string;
  occurredAt: string;
  registeredAt: string | null;
  observationSource: 'ami_event' | 'snapshot' | 'handle_hint_verified' | null;
  reasonCode: string | null;
};
